import type { QuotaUsage } from '@simplemon/lib';
import { MailWarning, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contactsApi } from '@/api/contacts';
import { quotasApi } from '@/api/quotas';
import { ContactsTable } from '@/components/contacts/ContactsTable';
import { ContactVerificationModal } from '@/components/contacts/ContactVerificationModal';
import { TestMessageModal } from '@/components/contacts/TestMessageModal';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { ContactWithStatus } from '@/types/contacts';

export function ContactsPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<ContactWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [verifyingContact, setVerifyingContact] = useState<ContactWithStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [contactQuota, setContactQuota] = useState<QuotaUsage | null>(null);
  const [quotaError, setQuotaError] = useState<string | null>(null);
  const [telegramLink, setTelegramLink] = useState<string | null>(null);
  const [telegramLinkContact, setTelegramLinkContact] = useState<string | null>(null);
  const [testContact, setTestContact] = useState<ContactWithStatus | null>(null);
  const [testNotice, setTestNotice] = useState<string | null>(null);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await contactsApi.getAll();
      setContacts(data);

      const quota = await quotasApi
        .get('contact')
        .then((result) => {
          setQuotaError(null);
          return result;
        })
        .catch((quotaErr) => {
          console.error('Failed to load contact quota:', quotaErr);
          setQuotaError('Unable to load quota usage');
          return null;
        });

      if (quota) {
        setContactQuota(quota);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleEdit = (contact: ContactWithStatus) => {
    navigate(`/dashboard/contacts/${contact.id}`);
  };

  const handleDelete = async (contact: ContactWithStatus) => {
    if (!confirm(`Are you sure you want to delete "${contact.name}"?`)) {
      return;
    }

    try {
      await contactsApi.delete(contact.id);
      await fetchContacts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete contact');
    }
  };

  const handleVerify = async (contact: ContactWithStatus) => {
    if (contact.verification_status === 'requested') {
      // Show code entry modal
      setVerifyingContact(contact);
    } else {
      // Request verification
      try {
        const result = await contactsApi.requestVerification(contact.id);
        if ('error' in result) {
          setError(result.message);
        } else {
          setVerifyingContact(contact);
          await fetchContacts();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to request verification');
      }
    }
  };

  const handleConnectTelegram = async (contact: ContactWithStatus) => {
    try {
      const result = await contactsApi.requestTelegramLink(contact.id);
      if ('error' in result) {
        setError(result.message);
        return;
      }
      setTelegramLink(result.deepLink);
      setTelegramLinkContact(contact.name);
      window.open(result.deepLink, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request Telegram link');
    }
  };

  const handleSendTest = (contact: ContactWithStatus) => {
    setTestNotice(null);
    setTestContact(contact);
  };

  const handleConfirmTest = async () => {
    if (!testContact) return;
    const result = await contactsApi.sendTestMessage(testContact.id);
    if ('error' in result) {
      throw new Error(result.message);
    }
    setTestContact(null);
    setTestNotice(result.note ?? result.message);
  };

  const handleVerificationSubmit = async (code: string) => {
    if (!verifyingContact) return;

    const result = await contactsApi.submitVerificationCode(verifyingContact.id, code);
    if ('error' in result) {
      throw new Error(result.message);
    }
    setVerifyingContact(null);
    await fetchContacts();
  };

  const handleResend = async () => {
    if (!verifyingContact) return;

    const result = await contactsApi.requestVerification(verifyingContact.id);
    if ('error' in result) {
      throw new Error(result.message);
    }
    await fetchContacts();
  };

  const canResend = (contact: ContactWithStatus) => {
    if (!contact.next_allowed_at) return true;
    return new Date() >= new Date(contact.next_allowed_at);
  };

  const quotaReached = contactQuota ? contactQuota.used >= contactQuota.limit : false;
  const hasAvailableMonitoringEmailContact = contacts.some(
    (contact) =>
      contact.type === 'email' &&
      contact.verification_status === 'verified' &&
      !contact.blocked_at &&
      contact.monitoringEmailStatus !== 'unsubscribed',
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading contacts...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
            <p className="text-gray-600 mt-1">
              Manage email, SMS, and Telegram contacts for notifications
            </p>
            {contactQuota ? (
              <p className="text-sm text-gray-500">
                {contactQuota.used} out of {contactQuota.limit} in use
              </p>
            ) : quotaError ? (
              <p className="text-sm text-gray-500">{quotaError}</p>
            ) : null}
          </div>
          <Button
            className="shrink-0"
            onClick={() => navigate('/dashboard/contacts/new')}
            disabled={quotaReached}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        </div>
        {quotaReached && contactQuota ? (
          <p className="mt-2 text-xs text-red-600">Quota reached. Remove a contact to add more.</p>
        ) : null}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {!error && !hasAvailableMonitoringEmailContact && (
        <Alert className="mb-4 border-amber-200 bg-amber-50 text-amber-900">
          <MailWarning className="h-4 w-4 text-amber-700" aria-hidden="true" />
          <AlertTitle>
            No email contacts are currently available for monitoring notifications
          </AlertTitle>
          <AlertDescription>
            Verify or add a subscribed email contact to receive monitoring emails. This notice
            applies only to email contacts.
          </AlertDescription>
        </Alert>
      )}

      {telegramLink && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg">
          <div className="font-medium">Telegram link ready</div>
          <div className="text-sm mt-1">
            {telegramLinkContact ? `Connect ${telegramLinkContact}: ` : 'Connect Telegram: '}
            <a href={telegramLink} className="underline" target="_blank" rel="noreferrer">
              {telegramLink}
            </a>
          </div>
        </div>
      )}

      {testNotice && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          {testNotice}
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <ContactsTable
          contacts={contacts}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onVerify={handleVerify}
          onConnectTelegram={handleConnectTelegram}
          onSendTest={handleSendTest}
        />
      </div>

      {verifyingContact && (
        <ContactVerificationModal
          contactId={verifyingContact.id}
          contactName={verifyingContact.name}
          onSubmit={handleVerificationSubmit}
          onCancel={() => setVerifyingContact(null)}
          onResend={handleResend}
          canResend={canResend(verifyingContact)}
          nextAllowedAt={
            verifyingContact.next_allowed_at
              ? new Date(verifyingContact.next_allowed_at)
              : undefined
          }
        />
      )}

      {testContact && (
        <TestMessageModal
          channel={
            testContact.type === 'email'
              ? 'email'
              : testContact.type === 'telegram'
                ? 'telegram'
                : 'sms'
          }
          destination={
            testContact.type === 'email'
              ? (testContact.email ?? '')
              : testContact.type === 'telegram'
                ? (testContact.telegram_chat_id ?? '')
                : (testContact.phone_number ?? '')
          }
          message="This is a test message from Valvo.io."
          note={
            testContact.type === 'phone'
              ? 'SMS delivery is not configured yet. This message will only be queued.'
              : undefined
          }
          onCancel={() => setTestContact(null)}
          onConfirm={handleConfirmTest}
        />
      )}
    </div>
  );
}
